import { supabase } from '../lib/supabase';
import { voucherSyncService } from './voucherSync';

export type ProjectRole = 'owner' | 'admin' | 'editor' | 'viewer';

export interface Project {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  description: string | null;
  settings: Record<string, unknown>;
  countries: string[];
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectMember {
  id: string;
  project_id: string;
  user_id: string;
  role: ProjectRole;
  permissions: Record<string, unknown>;
  joined_at: string;
  user?: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
}

export interface ProjectWithMembers extends Project {
  members: ProjectMember[];
  member_count: number;
}

export interface ProjectLimits {
  tenant_id: string;
  tenant_name: string;
  plan: string;
  plan_display_name: string;
  subscription_status: string;
  projects: {
    current: number;
    base_limit: number;
    addon_quantity: number;
    max: number;
    remaining: number;
    unlimited: boolean;
  };
  users: {
    current: number;
    max: number;
    remaining: number;
    unlimited: boolean;
  };
  addons: {
    projects: {
      quantity: number;
      price_per_slot: number;
      monthly_cost: number;
    };
    can_purchase: boolean;
  };
}

export interface CreateProjectData {
  tenant_id: string;
  name: string;
  description?: string;
  settings?: Record<string, unknown>;
  countries?: string[];
}

export interface UpdateProjectData {
  name?: string;
  description?: string;
  settings?: Record<string, unknown>;
  countries?: string[];
  is_active?: boolean;
}

export const projectsApi = {
  async fetchProjects(tenantId: string): Promise<Project[]> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .order('is_default', { ascending: false })
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async fetchAllProjects(tenantId: string, includeInactive = false): Promise<Project[]> {
    let query = supabase
      .from('projects')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('is_default', { ascending: false })
      .order('name');

    if (!includeInactive) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async fetchProject(projectId: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async fetchProjectWithMembers(projectId: string): Promise<ProjectWithMembers | null> {
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .maybeSingle();

    if (projectError) throw projectError;
    if (!project) return null;

    const { data: members, error: membersError } = await supabase
      .from('project_members')
      .select(`
        *,
        user:app_users (
          id,
          email,
          name,
          role
        )
      `)
      .eq('project_id', projectId)
      .order('role')
      .order('joined_at');

    if (membersError) throw membersError;

    return {
      ...project,
      members: members || [],
      member_count: members?.length || 0
    };
  },

  async createProject(data: CreateProjectData): Promise<Project> {
    const limitCheck = await this.checkProjectLimit(data.tenant_id);
    if (!limitCheck.allowed) {
      throw new Error(limitCheck.reason || 'Project limit reached');
    }

    const { data: project, error } = await supabase
      .from('projects')
      .insert({
        tenant_id: data.tenant_id,
        name: data.name,
        slug: crypto.randomUUID(),
        description: data.description || null,
        settings: data.settings || {},
        countries: data.countries || [],
        is_active: true,
        is_default: false
      })
      .select()
      .single();

    if (error) throw error;

    const projectCountries = project.countries || ['INT'];
    if (projectCountries.length > 0) {
      voucherSyncService.startBackgroundSync({
        tenantId: project.tenant_id,
        projectId: project.id,
        targetCountries: projectCountries,
        lang: 'en',
        currency: 'USD',
      }).then(result => {
        if (result.success) {
          console.log(`[ProjectsApi] Started voucher catalog sync for new project ${project.id}, job: ${result.jobId}`);
        } else {
          console.warn(`[ProjectsApi] Failed to start voucher sync for project ${project.id}:`, result.error);
        }
      }).catch(err => {
        console.warn(`[ProjectsApi] Error starting voucher sync for project ${project.id}:`, err);
      });
    }

    return project;
  },

  async updateProject(projectId: string, updates: UpdateProjectData): Promise<Project> {
    const { data, error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async archiveProject(projectId: string): Promise<void> {
    const { error } = await supabase
      .from('projects')
      .update({ is_active: false })
      .eq('id', projectId);

    if (error) throw error;
  },

  async deleteProject(projectId: string): Promise<void> {
    const { data: project } = await supabase
      .from('projects')
      .select('is_default')
      .eq('id', projectId)
      .maybeSingle();

    if (project?.is_default) {
      throw new Error('Cannot delete the default project');
    }

    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;
  },

  async fetchProjectMembers(projectId: string): Promise<ProjectMember[]> {
    const { data, error } = await supabase
      .from('project_members')
      .select(`
        *,
        user:app_users (
          id,
          email,
          name,
          role
        )
      `)
      .eq('project_id', projectId)
      .order('role')
      .order('joined_at');

    if (error) throw error;
    return data || [];
  },

  async addProjectMember(
    projectId: string,
    userId: string,
    role: ProjectRole = 'viewer'
  ): Promise<ProjectMember> {
    const { data, error } = await supabase
      .from('project_members')
      .insert({
        project_id: projectId,
        user_id: userId,
        role,
        permissions: {}
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateMemberRole(
    projectId: string,
    userId: string,
    role: ProjectRole
  ): Promise<ProjectMember> {
    const { data, error } = await supabase
      .from('project_members')
      .update({ role })
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async removeProjectMember(projectId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('project_members')
      .delete()
      .eq('project_id', projectId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  async checkProjectLimit(tenantId: string): Promise<{ allowed: boolean; reason?: string; current_count?: number; max_allowed?: number; remaining?: number }> {
    const { data, error } = await supabase
      .rpc('check_project_limit', { p_tenant_id: tenantId });

    if (error) throw error;
    return data;
  },

  async checkUserLimit(tenantId: string): Promise<{ allowed: boolean; reason?: string; current_count?: number; max_allowed?: number; remaining?: number }> {
    const { data, error } = await supabase
      .rpc('check_user_limit', { p_tenant_id: tenantId });

    if (error) throw error;
    return data;
  },

  async getTenantLimits(tenantId: string): Promise<ProjectLimits> {
    const { data, error } = await supabase
      .rpc('get_tenant_limits', { p_tenant_id: tenantId });

    if (error) throw error;
    return data;
  },

  async getUserProjectAccess(userId: string): Promise<{ projectId: string; role: ProjectRole }[]> {
    const { data, error } = await supabase
      .from('project_members')
      .select('project_id, role')
      .eq('user_id', userId);

    if (error) throw error;
    return (data || []).map(d => ({ projectId: d.project_id, role: d.role as ProjectRole }));
  },

  async getDefaultProject(tenantId: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('is_default', true)
      .maybeSingle();

    if (error) throw error;
    return data;
  }
};
